/**
 * Strip credentials out of a URL path before it reaches any analytics sink.
 *
 * The public booking routes are `/book/:token` and `/book/:token/manage`, and that token IS the
 * authorisation — anyone holding it can read a candidate's interview, move it, or cancel it. So it
 * must never be recorded anywhere, and `window.location.pathname` on those pages contains nothing
 * else.
 *
 * `AnalyticsPageTracker` already declines to send pageviews for `/book/*`, but that is only ONE
 * caller. The global error net (`installErrorTracking`) and the usage sink both stamp the current
 * pathname onto events from EVERY page, including the public ones, and neither goes through the
 * pageview guard. This function is the single chokepoint they share, so a new sink that records a
 * path cannot reintroduce the leak by forgetting a special case.
 *
 * Redaction is by POSITION, not by pattern: the token is whatever sits in that slot, and matching
 * on "looks like a token" would fail open the first time a token format changes.
 */
export function redactPath(path: string): string {
  if (!path) return path;
  const segments = path.split('/');
  // ['', 'book', '<token>', ...] — replace the token slot and keep the rest of the shape, so
  // `/book/:token` and `/book/:token/manage` stay distinguishable in analytics.
  // `/book/s/<shareToken>` is the LinkedIn link — a credential too (anyone holding it can book),
  // one slot further along. A personal token is never the single letter `s`.
  if (segments[1] === 'book' && segments[2] === 's' && segments.length > 3) {
    segments[3] = ':shareToken';
    return segments.join('/');
  }
  if (segments[1] === 'book' && segments.length > 2 && segments[2]) {
    segments[2] = ':token';
    return segments.join('/');
  }
  // The Talent Community's own links: `/join/confirm/:token` and `/join/manage/:token`. Same rule —
  // that token opens a person's data and deletes it.
  if (segments[1] === 'join' && (segments[2] === 'confirm' || segments[2] === 'manage') && segments[3]) {
    segments[3] = ':token';
    return segments.join('/');
  }
  return path;
}

/** Current pathname, already redacted. Safe default for any sink that records where an event happened. */
export function currentPath(): string {
  if (typeof window === 'undefined') return '';
  return redactPath(window.location.pathname);
}

/**
 * Same redaction for a value that may be a FULL URL rather than a bare path.
 *
 * SDK-injected properties (Amplitude's `[Amplitude] Page URL`, referrers) carry absolute URLs, and
 * `redactPath` alone would miss them — in `https://host/book/TOKEN` the token is not in the slot
 * `redactPath` looks at. Anything unparseable falls back to path redaction, so a partial or
 * relative value is still scrubbed rather than passed through.
 */
export function redactUrlLike(value: string): string {
  if (!value || !(value.includes('/book/') || value.includes('/join/'))) return value;
  try {
    const url = new URL(value);
    // An outreach InMail's `r=` on the LinkedIn link names one person's InMail (Nov 2 2026): it goes too.
    if (url.pathname.startsWith('/book/s/')) url.searchParams.delete('r');
    url.pathname = redactPath(url.pathname);
    return url.toString();
  } catch {
    return redactPath(value);
  }
}
